import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Campaign } from '../types';
import CampaignForm from '../components/CampaignForm';
import StatsModal from '../components/StatsModal';
import {
  AdminSessionError,
  createCampaign,
  deleteCampaign,
  listCampaigns,
  updateCampaign,
} from '../lib/adminApi';

type View = 'list' | 'create' | 'edit';

const DashboardPage: React.FC = () => {
  const { logout } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('list');
  const [editTarget, setEditTarget] = useState<Campaign | null>(null);
  const [statsTarget, setStatsTarget] = useState<Campaign | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCampaigns(await listCampaigns());
    } catch (err) {
      if (err instanceof AdminSessionError) return;
      setError('Unable to load campaigns.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleCreate = async (data: Omit<Campaign, 'id'>) => {
    setSaving(true);
    try {
      await createCampaign(data);
      showToast('Campaign created successfully');
      setView('list');
      fetchCampaigns();
    } catch (err) {
      if (!(err instanceof AdminSessionError)) setError('Unable to save campaign.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (data: Omit<Campaign, 'id'>) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await updateCampaign(editTarget.id, data);
      showToast('Campaign updated successfully');
      setView('list');
      setEditTarget(null);
      fetchCampaigns();
    } catch (err) {
      if (!(err instanceof AdminSessionError)) setError('Unable to save campaign.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (campaign: Campaign) => {
    try {
      await updateCampaign(campaign.id, { active: !campaign.active });
      showToast(`Campaign ${!campaign.active ? 'activated' : 'paused'}`);
      fetchCampaigns();
    } catch (err) {
      if (!(err instanceof AdminSessionError)) setError('Unable to update campaign.');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Delete campaign for ${name}? This cannot be undone.`)) return;
    try {
      await deleteCampaign(id);
      showToast('Campaign deleted');
      fetchCampaigns();
    } catch (err) {
      if (!(err instanceof AdminSessionError)) setError('Unable to delete campaign.');
    }
  };

  const startEdit = (c: Campaign) => {
    setEditTarget(c);
    setView('edit');
  };

  const activeCount = campaigns.filter(c => c.active).length;

  return (
    <div style={s.root}>
      <nav style={s.sidebar}>
        <div style={s.sideTop}>
          <div style={s.logoWrap}>
            <span style={s.logoIcon}>🚗</span>
            <div>
              <div style={s.logoTitle}>Friends in Cars</div>
              <div style={s.logoSub}>Advertiser Portal</div>
            </div>
          </div>
          <div style={s.navSection}>
            <div style={s.navLabel}>Campaign Management</div>
            <button
              style={{ ...s.navBtn, ...(view === 'list' ? s.navBtnActive : {}) }}
              onClick={() => { setView('list'); setEditTarget(null); }}
            >
              <span style={s.navIcon}>▦</span> All Campaigns
            </button>
            <button
              style={{ ...s.navBtn, ...(view === 'create' ? s.navBtnActive : {}) }}
              onClick={() => { setView('create'); setEditTarget(null); }}
            >
              <span style={s.navIcon}>+</span> New Campaign
            </button>
          </div>
        </div>
        <div style={s.sideBottom}>
          <div style={s.summaryBox}>
            <div style={s.summaryRow}>
              <span style={s.summaryLabel}>Active</span>
              <span style={s.summaryVal}>{activeCount}</span>
            </div>
            <div style={s.summaryRow}>
              <span style={s.summaryLabel}>Total</span>
              <span style={s.summaryVal}>{campaigns.length}</span>
            </div>
          </div>
          <button onClick={logout} style={s.logoutBtn}>← Sign Out</button>
        </div>
      </nav>

      <main style={s.main}>
        {toast && <div style={s.toast}>{toast}</div>}
        {error && (
          <div style={s.errorBar}>
            {error}
            <button onClick={() => setError('')} style={s.errorClose}>✕</button>
          </div>
        )}

        {view === 'list' && (
          <div>
            <div style={s.pageHeader}>
              <div>
                <h1 style={s.h1}>Campaigns</h1>
                <p style={s.h1Sub}>{campaigns.length} total · {activeCount} active</p>
              </div>
              <button onClick={() => setView('create')} style={s.btnPrimary}>
                + New Campaign
              </button>
            </div>
            {loading ? (
              <div style={s.loadingMsg}>Loading campaigns…</div>
            ) : campaigns.length === 0 ? (
              <div style={s.empty}>
                <div style={s.emptyIcon}>📣</div>
                <div style={s.emptyTitle}>No campaigns yet</div>
                <div style={s.emptyText}>Create your first campaign to start reaching FiC riders.</div>
                <button onClick={() => setView('create')} style={{ ...s.btnPrimary, marginTop: 20 }}>
                  Create First Campaign
                </button>
              </div>
            ) : (
              <div style={s.table}>
                <div style={s.tableHeader}>
                  <span style={s.th}>Advertiser</span>
                  <span style={s.th}>Offer</span>
                  <span style={s.th}>Code</span>
                  <span style={s.th}>Bid</span>
                  <span style={s.th}>Topics</span>
                  <span style={s.th}>Status</span>
                  <span style={s.th}>Actions</span>
                </div>
                {campaigns.map(c => (
                  <CampaignRow
                    key={c.id}
                    campaign={c}
                    onEdit={() => startEdit(c)}
                    onToggle={() => toggleActive(c)}
                    onStats={() => setStatsTarget(c)}
                    onDelete={() => handleDelete(c.id, c.advertiser_name)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {view === 'create' && (
          <div style={s.formWrap}>
            <div style={s.formHeader}>
              <button onClick={() => setView('list')} style={s.backBtn}>← Back to Campaigns</button>
              <h1 style={s.h1}>New Campaign</h1>
            </div>
            <div style={s.formCard}>
              <CampaignForm
                onSave={handleCreate}
                onCancel={() => setView('list')}
                saving={saving}
              />
            </div>
          </div>
        )}

        {view === 'edit' && editTarget && (
          <div style={s.formWrap}>
            <div style={s.formHeader}>
              <button onClick={() => { setView('list'); setEditTarget(null); }} style={s.backBtn}>← Back to Campaigns</button>
              <h1 style={s.h1}>Edit Campaign</h1>
              <p style={s.h1Sub}>{editTarget.advertiser_name}</p>
            </div>
            <div style={s.formCard}>
              <CampaignForm
                initial={editTarget}
                onSave={handleEdit}
                onCancel={() => { setView('list'); setEditTarget(null); }}
                saving={saving}
              />
            </div>
          </div>
        )}
      </main>

      {statsTarget && (
        <StatsModal campaign={statsTarget} onClose={() => setStatsTarget(null)} />
      )}
    </div>
  );
};

const CampaignRow = ({ campaign: c, onEdit, onToggle, onStats, onDelete }: {
  campaign: Campaign;
  onEdit: () => void;
  onToggle: () => void;
  onStats: () => void;
  onDelete: () => void;
}) => {
  const topics = c.topic_categories
    ? c.topic_categories.split(',').map(t => t.trim()).filter(Boolean)
    : [];

  return (
    <div style={rs.row}>
      <div style={rs.cell}>
        <div style={rs.advertiserName}>{c.advertiser_name}</div>
        <a href={c.advertiser_url} target="_blank" rel="noreferrer" style={rs.url}>
          {c.advertiser_url.replace('https://', '').replace('http://', '')}
        </a>
      </div>
      <div style={rs.cell}>
        <div style={rs.offerText}>{c.sbic_offer_text}</div>
      </div>
      <div style={rs.cell}>
        <code style={rs.code}>{c.sbic_discount_code}</code>
        <div style={rs.expiry}>{c.sbic_expiry_hours}h expiry</div>
      </div>
      <div style={rs.cell}>
        <div style={rs.bid}>${c.bid_rate_usd}</div>
        <div style={rs.bidLabel}>per impression</div>
      </div>
      <div style={rs.cell}>
        <div style={rs.topics}>
          {topics.slice(0, 2).map(t => <span key={t} style={rs.topic}>{t}</span>)}
          {topics.length > 2 && <span style={rs.topicMore}>+{topics.length - 2}</span>}
        </div>
      </div>
      <div style={rs.cell}>
        <span style={{ ...rs.badge, ...(c.active ? rs.badgeActive : rs.badgePaused) }}>
          {c.active ? '● Active' : '○ Paused'}
        </span>
      </div>
      <div style={{ ...rs.cell, ...rs.actions }}>
        <button onClick={onStats} style={rs.actionBtn} title="Stats">📊</button>
        <button onClick={onEdit} style={rs.actionBtn} title="Edit">✏️</button>
        <button
          onClick={onToggle}
          style={{ ...rs.actionBtn, color: c.active ? '#d97706' : '#16a34a' }}
          title={c.active ? 'Pause' : 'Activate'}
        >
          {c.active ? '⏸' : '▶'}
        </button>
        <button onClick={onDelete} style={{ ...rs.actionBtn, color: '#dc2626' }} title="Delete">🗑</button>
      </div>
    </div>
  );
};

const rs: Record<string, React.CSSProperties> = {
  row: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 2fr 1.2fr 0.8fr 1fr 0.8fr 1fr',
    padding: '16px 24px',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'center',
    background: '#ffffff',
    transition: 'background 0.1s',
    gap: 12,
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minWidth: 0,
  },
  advertiserName: {
    color: '#111827',
    fontSize: 14,
    fontWeight: 600,
  },
  url: {
    color: '#9ca3af',
    fontSize: 11,
    textDecoration: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  offerText: {
    color: '#374151',
    fontSize: 13,
    lineHeight: 1.4,
  },
  code: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    padding: '2px 7px',
    fontSize: 12,
    color: '#111827',
    fontFamily: "'JetBrains Mono', monospace",
    width: 'fit-content',
  },
  expiry: {
    color: '#9ca3af',
    fontSize: 11,
  },
  bid: {
    color: '#111827',
    fontSize: 15,
    fontWeight: 700,
  },
  bidLabel: {
    color: '#9ca3af',
    fontSize: 11,
  },
  topics: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  topic: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 20,
    color: '#374151',
    padding: '2px 8px',
    fontSize: 11,
  },
  topicMore: {
    color: '#9ca3af',
    fontSize: 11,
    padding: '2px 4px',
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    width: 'fit-content',
  },
  badgeActive: {
    background: '#dcfce7',
    color: '#16a34a',
  },
  badgePaused: {
    background: '#f3f4f6',
    color: '#6b7280',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  actionBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '6px 8px',
    borderRadius: 6,
    fontSize: 14,
    color: '#6b7280',
    transition: 'background 0.1s',
  },
};

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    background: '#f4f5f7',
    display: 'flex',
    fontFamily: "'Inter', sans-serif",
  },
  sidebar: {
    width: 240,
    background: '#ffffff',
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '28px 16px',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  sideTop: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    paddingLeft: 8,
  },
  logoIcon: { fontSize: 22 },
  logoTitle: {
    color: '#111827',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '-0.01em',
  },
  logoSub: {
    color: '#9ca3af',
    fontSize: 10,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  navSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  navLabel: {
    color: '#9ca3af',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    paddingLeft: 8,
    marginBottom: 4,
  },
  navBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    padding: '9px 12px',
    borderRadius: 8,
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  navBtnActive: {
    background: '#f3f4f6',
    color: '#111827',
    fontWeight: 600,
  },
  navIcon: { fontSize: 14 },
  sideBottom: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  summaryBox: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: 12,
  },
  summaryVal: {
    color: '#111827',
    fontSize: 14,
    fontWeight: 700,
  },
  logoutBtn: {
    background: 'transparent',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    color: '#6b7280',
    padding: '8px 12px',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    fontWeight: 500,
  },
  main: {
    flex: 1,
    padding: '36px 40px',
    overflowY: 'auto',
    position: 'relative',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },
  h1: {
    color: '#111827',
    fontSize: 24,
    fontWeight: 800,
    margin: '0 0 4px 0',
    letterSpacing: '-0.02em',
  },
  h1Sub: {
    color: '#9ca3af',
    fontSize: 13,
    margin: 0,
  },
  btnPrimary: {
    background: '#111827',
    border: 'none',
    borderRadius: 8,
    color: '#ffffff',
    padding: '11px 20px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    letterSpacing: '-0.01em',
  },
  table: {
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 2fr 1.2fr 0.8fr 1fr 0.8fr 1fr',
    padding: '12px 24px',
    borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb',
    gap: 12,
  },
  th: {
    color: '#6b7280',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  loadingMsg: {
    color: '#9ca3af',
    padding: 40,
    textAlign: 'center',
    fontSize: 14,
  },
  empty: {
    textAlign: 'center',
    padding: '80px 40px',
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e5e7eb',
  },
  emptyIcon: { fontSize: 40, marginBottom: 16 },
  emptyTitle: {
    color: '#111827',
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 8,
  },
  emptyText: { color: '#6b7280', fontSize: 14 },
  formWrap: { maxWidth: 720 },
  formHeader: {
    marginBottom: 28,
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 500,
    padding: '0 0 12px 0',
    display: 'block',
  },
  formCard: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 32,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  toast: {
    position: 'fixed',
    bottom: 28,
    right: 28,
    background: '#111827',
    color: '#ffffff',
    borderRadius: 8,
    padding: '12px 20px',
    fontSize: 13,
    fontWeight: 600,
    zIndex: 200,
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    animation: 'fadeIn 0.2s ease',
  },
  errorBar: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '12px 16px',
    color: '#dc2626',
    fontSize: 13,
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  errorClose: {
    background: 'transparent',
    border: 'none',
    color: '#dc2626',
    cursor: 'pointer',
    fontSize: 14,
    fontFamily: 'inherit',
  },
};

export default DashboardPage;